import { useTranslation } from 'react-i18next';
import { fmtDateTime, relTime } from '../../lib/format';
import type { PipelineMessage } from './queries';

type Params = Record<string, string | number>;

/**
 * Words the API's machine-readable pipeline messages in the UI language (`msg.<code>` in the
 * `pipeline` catalog). The API still sends its English prose next to the codes — that is the
 * fallback when a message list is empty. Params: `at` (ISO) becomes `{{age}}`, `entity` the
 * plural entity name, `mode` the job-mode label; numbers are formatted by `{{x, number}}` in the
 * strings themselves so the grouping follows the language.
 */
export function usePipelineText() {
  const { t } = useTranslation(['pipeline', 'common']);
  const { t: tc } = useTranslation();

  const resolve = (params: PipelineMessage['params']): Params => {
    const out: Params = {};
    for (const [key, value] of Object.entries(params ?? {})) {
      if (key === 'at' && typeof value === 'string') out.age = relTime(value, tc) ?? fmtDateTime(value);
      else if (key === 'entity' && typeof value === 'string') {
        out.entity = t(`entityPlural.${value}`, { defaultValue: value });
      } else if (key === 'mode' && typeof value === 'string') {
        out.mode = t(`common:mode.${value}`, { defaultValue: value });
      } else out[key] = value;
    }
    return out;
  };
  const one = (m: PipelineMessage): string => t(`msg.${m.code}`, resolve(m.params));
  /** Joined parts (" · ") or the API's prose when there is nothing to word. */
  const text = (msgs: PipelineMessage[] | PipelineMessage | null | undefined, fallback: string): string => {
    const list = Array.isArray(msgs) ? msgs : msgs ? [msgs] : [];
    return list.length > 0 ? list.map(one).join(' · ') : fallback;
  };
  return { t, text };
}
